import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import { apiRequest, getApiBaseUrl } from '../lib/api';
import { getInitials } from '../utils/validation';

export default function PublicScanPage() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [leadSaved, setLeadSaved] = useState(false);
  const [leadError, setLeadError] = useState('');
  const scanLogged = useRef(false);
  const [lead, setLead] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    company: ''
  });

  useEffect(() => {
    scanLogged.current = false;
    setLeadSaved(false);
    setLeadError('');

    let cancelled = false;
    apiRequest(`/cards/${slug}`, { token: null })
      .then((data) => {
        if (!cancelled) {
          setCard(data);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('missing');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!card || scanLogged.current) {
      return;
    }

    scanLogged.current = true;
    apiRequest(`/cards/${slug}/scan`, { method: 'POST', body: {}, token: null }).catch(() => {});
  }, [card, slug]);

  const visibleLinks = useMemo(() => card?.links || [], [card]);

  const handleSaveContact = () => {
    window.location.href = `${getApiBaseUrl()}/cards/${slug}/vcard`;
  };

  const updateLeadField = (field, value) => {
    setLead((prev) => ({ ...prev, [field]: value }));
  };

  const submitLead = async (event) => {
    event.preventDefault();

    if (!lead.name.trim() || !lead.email.trim()) {
      setLeadError('Name and email are required before sharing your details.');
      return;
    }

    setLeadError('');
    setSaving(true);
    try {
      await apiRequest(`/cards/${slug}/scan`, { method: 'POST', body: lead, token: null });
      setLeadSaved(true);
      setLead({
        name: '',
        email: '',
        phone: '',
        title: '',
        company: ''
      });
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading') {
    return <div className="auth-page"><div className="auth-container"><p>Loading card...</p></div></div>;
  }

  if (status === 'missing') {
    return <div className="auth-page"><div className="auth-container"><p>This profile could not be found.</p></div></div>;
  }

  const initials = getInitials(card.name || '');

  return (
    <div className="auth-page" style={{ padding: '48px 16px' }}>
      <div
        style={{
          width: '100%',
          maxWidth: 1120,
          margin: '0 auto',
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'
        }}
      >
        <div className="scan-preview-frame" style={{ margin: 0 }}>
          <div className="scan-header">
            <div className="scan-avatar">
              {card.photo ? <img src={card.photo} alt={card.name} /> : <span className="initials">{initials}</span>}
            </div>
            <div className="scan-user-info">
              <div className="scan-name">{card.name}</div>
              <div className="scan-role">{[card.title, card.company].filter(Boolean).join(' - ')}</div>
              {card.tagline && <div className="scan-company">{card.tagline}</div>}
            </div>
          </div>

          {card.about && <div className="scan-about">{card.about}</div>}

          <div className="scan-actions">
            <button className="scan-action-primary" onClick={handleSaveContact}>
              {Icons.save} Save contact
            </button>
            {card.whatsapp && (
              <a href={`https://wa.me/${card.whatsapp.replace(/[^\d+]/g, '')}`} className="scan-action-wa" style={{ textDecoration: 'none' }}>
                {Icons.whatsapp} WhatsApp
              </a>
            )}
          </div>

          <div className="scan-secondary">
            <a href={`mailto:${card.email}`} className="scan-btn-sec" style={{ textDecoration: 'none' }}>
              <span className="sc-label">Email</span>
              <span className="sc-value">{card.email}</span>
            </a>
            <a href={`tel:${card.phone}`} className="scan-btn-sec" style={{ textDecoration: 'none' }}>
              <span className="sc-label">Call</span>
              <span className="sc-value">{card.phone}</span>
            </a>
          </div>

          {visibleLinks.length > 0 && (
            <div className="scan-links">
              {visibleLinks.map((link) => (
                <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="scan-link-row" style={{ textDecoration: 'none' }}>
                  <span className="scan-link-label">{link.platform}</span>
                  <span className="scan-link-value">Open -&gt;</span>
                </a>
              ))}
            </div>
          )}

          <div className="scan-footer">
            <AscendingK size={12} color="#B8B2A2" />
            <span className="scan-footer-text">Powered by Connekted in</span>
          </div>
        </div>

        <div className="panel">
          <div className="panel-label">Leave your details</div>
          <h2 className="page-title" style={{ fontSize: 36, marginBottom: 8 }}>
            Turn a scan into a warm <em>follow-up</em>.
          </h2>
          <p className="page-subtitle" style={{ marginBottom: 20 }}>
            If you met {card.name?.split(' ')[0]}, you can drop your details here so the scan lands in their CRM with context instead of as an anonymous visit.
          </p>
          {leadSaved ? (
            <div className="save-errors" style={{ background: 'rgba(10, 163, 102, 0.1)', color: 'var(--success)' }}>
              {Icons.check}
              <span>Your details were shared successfully.</span>
            </div>
          ) : (
            <form onSubmit={submitLead}>
              {leadError && (
                <div className="save-errors">
                  {Icons.warning}
                  <span>{leadError}</span>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={lead.name} onChange={(event) => updateLeadField('name', event.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={lead.email} onChange={(event) => updateLeadField('email', event.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={lead.phone} onChange={(event) => updateLeadField('phone', event.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <input className="form-input" value={lead.company} onChange={(event) => updateLeadField('company', event.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={lead.title} onChange={(event) => updateLeadField('title', event.target.value)} />
              </div>
              <button className="btn btn-primary-app" style={{ marginTop: 8 }} disabled={saving}>
                {saving ? 'Saving...' : 'Share my details'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
